import { getJbpKeyInitiativeCrud } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives'
import { requestCommitmentStatuses } from 'legacy/lut/features/partner360/commitment'
import {
  requestPartnerData,
  selectFinancialYear,
  selectPartner360Data,
  selectPartnerAccess,
  selectProgramType,
} from 'legacy/lut/features/partner360/configuration'
import {
  CommitmentReminderActionTypeEnum,
  ICommitmentReminderActionItem,
} from 'legacy/lut/services/partner360/commitment/models/ICommitmentReminder'
import { type ComponentType, useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { useAppDispatch } from 'cng-common/hooks/useAppDispatch'
import { getUrlParams, getUrlWithParams } from 'core-components/helpers/url'
import { selectRouteParams } from 'core-components/sitebuilder/store/selectors/selectRouteParams'
import { useNavigate } from 'core/hooks/useNavigate'
import { ActionsPopup } from 'partner360/components/commitmentReminder/components/planReviewActions/components/actionsPopup/ActionsPopup'
import {
  FINANCIAL_SECTION_URL_VALUE,
  P360_GLOBAL_PAGE_URL,
  P360_PAGE_URL,
  PLANNING_METRIC_URL_SEGMENT,
  PLANNING_SECTION_URL_SEGMENT,
} from 'partner360/configs'
import {
  normalizeFinancialMetricMode,
  parseFinancialMetricModes,
} from 'partner360/entities/financialMetricMode'
import { p360TabsIds } from 'partner360/entities/partnerCompleteness'
import { useGetPlanFinancialsQuery } from 'partner360/entities/planFinancials'
import { AcknowledgmentStatusDialog } from 'partner360/features/acknowledgmentStatusDialog'
import { KeyInitiativesReviewModal } from 'partner360/features/keyInitiativesReview'
import { PartnerDevicesInfoModal } from 'partner360/features/partnerDevicesInfo'
import { SobActualsModal } from 'partner360/features/sobActualsReview'
import type { TnetReviewModalProps } from 'partner360/features/tnetReviewModal/model/types'
import { TnetReviewModal as TnetReviewModalComponent } from 'partner360/features/tnetReviewModal/ui/TnetReviewModal'
import { scrollToElement } from 'partner360/helpers/scrollToElement'
import { partner360WidgetsBus } from 'partner360/lib/partner360WidgetsBus'
import {
  normalizePlanReviewActionParams,
  resolvePlanReviewActualsValueTypeActionParam,
  resolvePlanReviewBySubCategoryActionParam,
  resolvePlanReviewCategoryActionParam,
} from 'partner360/lib/planReviewActionParams'
import { NetworkDebugSource } from 'shared/lib/networkDebug'

import { PlanReviewKeyInitiative } from './components/planReviewKeyInitiative/PlanReviewKeyInitiative'

interface IPlanReviewActionsProps {
  actions: ICommitmentReminderActionItem[]
  additionalLinkParams?: Record<string, string>
  onActionExecuted?: () => void
  shortView?: boolean
  isDisabled?: boolean
}

const TnetReviewModal = TnetReviewModalComponent as ComponentType<TnetReviewModalProps>

export const PlanReviewActions = ({
  actions,
  additionalLinkParams,
  onActionExecuted,
  shortView,
  isDisabled,
}: IPlanReviewActionsProps) => {
  const [selectedAction, setSelectedAction] = useState<ICommitmentReminderActionItem | null>(null)
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>()
  const navigate = useNavigate()

  const partner360Data = useSelector(selectPartner360Data)
  const { isDistributor } = useSelector(selectPartnerAccess)
  const programType = useSelector(selectProgramType)
  const fyear = useSelector(selectFinancialYear)

  const dispatch = useAppDispatch()

  const routeParams = useSelector(selectRouteParams)

  const isSobActionSelected =
    selectedAction?.ActionType === CommitmentReminderActionTypeEnum.JbpPatSob

  const shouldFetchFinData =
    isSobActionSelected ||
    selectedAction?.ActionType === CommitmentReminderActionTypeEnum.JbpPatTnet

  const selectedActionParams = useMemo(
    () => normalizePlanReviewActionParams(selectedAction?.ActionParam),
    [selectedAction?.ActionParam]
  )
  const isSobBySubCategory =
    isSobActionSelected && resolvePlanReviewBySubCategoryActionParam(selectedActionParams)

  const {
    data = [],
    isLoading,
    isFetching,
  } = useGetPlanFinancialsQuery(
    {
      partnerId: partner360Data.partnerId,
      fYear: fyear,
      programType,
      category: selectedCategory,
      isRollUp: false,
      // SOB review asks for the SubCategory/L3Category breakdown only when the action requires it
      isBySubCategory: isSobBySubCategory,
    },
    {
      skip: !partner360Data.partnerId || !fyear || !programType || !shouldFetchFinData,
    }
  )

  const isP360Page = useMemo(() => {
    return [P360_PAGE_URL, P360_GLOBAL_PAGE_URL].includes(routeParams.page)
  }, [routeParams.page])

  const selectedActionCategory = resolvePlanReviewCategoryActionParam(selectedActionParams)
  const selectedActionActualsValueType =
    resolvePlanReviewActualsValueTypeActionParam(selectedActionParams)

  const finData = useMemo(() => {
    return data.map((el) => ({ ...el, Category: el.Category || selectedActionCategory }))
  }, [data, selectedActionCategory])

  const metricMode = normalizeFinancialMetricMode(data[0]?.NetNdp) ?? 'net'
  const reviewMetricMode =
    normalizeFinancialMetricMode(selectedActionActualsValueType) ?? metricMode
  const availableMetricModes = useMemo(() => {
    const selectedActionMetricMode = normalizeFinancialMetricMode(selectedActionActualsValueType)

    if (selectedActionMetricMode) {
      return [selectedActionMetricMode]
    }

    return parseFinancialMetricModes(data[0]?.NetNdp)
  }, [data, selectedActionActualsValueType])

  const goTo = useCallback(
    (url: string) => {
      scrollToElement(p360TabsIds)
      const params = getUrlParams(url)
      const newUrl = getUrlWithParams({
        ...additionalLinkParams,
        ...params,
      })
      navigate(newUrl)
    },
    [additionalLinkParams, navigate]
  )

  const resetData = useCallback(
    (action: ICommitmentReminderActionItem | null, needFetchFinData?: boolean) => {
      if (action?.ActionType === CommitmentReminderActionTypeEnum.JBPPatInitiative) {
        getJbpKeyInitiativeCrud().removeQueries(true)
      }

      if (action?.ActionType === CommitmentReminderActionTypeEnum.JBPPatMif) {
        dispatch(
          requestPartnerData({
            partnerId: partner360Data.partnerId,
            fYear: fyear,
            force: true,
          })
        )
      }

      if (
        action?.ActionType &&
        [
          CommitmentReminderActionTypeEnum.JbpPatSob,
          CommitmentReminderActionTypeEnum.JbpPatTnet,
        ].includes(action.ActionType)
      ) {
        partner360WidgetsBus.notify('planningRefresh')
      }

      if (needFetchFinData) {
        const actionParams = normalizePlanReviewActionParams(action?.ActionParam)

        setSelectedCategory(resolvePlanReviewCategoryActionParam(actionParams))
      }
    },
    [dispatch, fyear, partner360Data.partnerId]
  )

  const onClose = useCallback(() => {
    setSelectedAction(null)
    setSelectedCategory(undefined)
  }, [])

  const closeAcknowledgeAction = useCallback(() => {
    setSelectedAction(null)
    if (onActionExecuted) {
      onActionExecuted()
    }
  }, [onActionExecuted])

  const goByActionLink = useMemo(() => {
    if (!selectedAction?.ActionLink) {
      return undefined
    }

    return () => {
      if (selectedAction?.ActionLink) {
        goTo(selectedAction.ActionLink)
        onClose()
      }
    }
  }, [goTo, onClose, selectedAction])

  const goToPlanning = useCallback(() => {
    const categoryPath = selectedActionCategory
      ? `/${PLANNING_SECTION_URL_SEGMENT}/${selectedActionCategory}`
      : ''

    goTo(
      `jbp-tab/${FINANCIAL_SECTION_URL_VALUE}${categoryPath}/${PLANNING_METRIC_URL_SEGMENT}/${reviewMetricMode}`
    )
    onClose()
  }, [goTo, onClose, reviewMetricMode, selectedActionCategory])

  const onActionExecute = useCallback(
    (action: ICommitmentReminderActionItem | null, needFetchFinData?: boolean) => async () => {
      dispatch(
        requestCommitmentStatuses({
          partnerId: partner360Data.partnerId,
          fYear: fyear,
        })
      )

      resetData(action, needFetchFinData)

      if (onActionExecuted) {
        onActionExecuted()
      }
    },
    [dispatch, fyear, onActionExecuted, partner360Data.partnerId, resetData]
  )

  const onSelectAction = useCallback(
    async (action: ICommitmentReminderActionItem) => {
      if (action?.ActionBlockedBy) {
        return
      }

      if (
        action?.ActionLink &&
        ![
          CommitmentReminderActionTypeEnum.JBPPatKi,
          CommitmentReminderActionTypeEnum.JBPPatAcknowledgment,
        ].includes(action.ActionType)
      ) {
        goTo(action.ActionLink)
      } else if (
        action.ActionType === CommitmentReminderActionTypeEnum.JBPPatAcknowledgment &&
        isP360Page &&
        action?.ActionLink
      ) {
        goTo(action.ActionLink)
      } else {
        const actionParams = normalizePlanReviewActionParams(action.ActionParam)
        const actionCategory = resolvePlanReviewCategoryActionParam(actionParams)

        if (
          [
            CommitmentReminderActionTypeEnum.JbpPatSob,
            CommitmentReminderActionTypeEnum.JbpPatTnet,
          ].includes(action.ActionType)
        ) {
          setSelectedCategory(actionCategory)
        }
        setSelectedAction(action)
      }
    },
    [goTo, isP360Page]
  )

  if (!actions.length) {
    return null
  }

  return (
    <div>
      <ActionsPopup
        actions={actions}
        onSelectAction={onSelectAction}
        isLoading={isLoading || isFetching}
        selectedAction={selectedAction}
        shortView={shortView}
        isDisabled={isDisabled}
      />
      <TnetReviewModal
        show={selectedAction?.ActionType === CommitmentReminderActionTypeEnum.JbpPatTnet}
        partner={{
          region: partner360Data.geoRegion ?? '',
          country: partner360Data.country,
          partnerName: partner360Data.partnerName,
          pid: partner360Data.partnerId,
          locationId: partner360Data.locationId,
        }}
        category={selectedActionCategory || null}
        finRows={data}
        metricMode={metricMode}
        reviewMetricMode={reviewMetricMode}
        currentFiscalYear={fyear}
        programType={programType}
        onClose={onClose}
        onGoToPlanning={goToPlanning}
        onConfirmed={onActionExecute(selectedAction, true)}
      />
      <SobActualsModal
        show={isSobActionSelected}
        pid={partner360Data.partnerId}
        offering={programType}
        region={partner360Data.geoRegion ?? ''}
        country={partner360Data.country ?? ''}
        partnerName={partner360Data.partnerName ?? ''}
        locationId={partner360Data.locationId ?? ''}
        category={selectedActionCategory || null}
        isBySubCategory={isSobBySubCategory}
        isNoPlanEditable={false}
        defaultMetricMode={reviewMetricMode}
        availableMetricModes={availableMetricModes}
        currentFiscalYear={fyear}
        finData={finData}
        onClose={onClose}
        onConfirmed={onActionExecute(selectedAction, true)}
      />
      <PartnerDevicesInfoModal
        show={selectedAction?.ActionType === CommitmentReminderActionTypeEnum.JBPPatMif}
        partnerId={partner360Data.partnerId}
        offering={programType}
        fYear={fyear}
        isEditable={true}
        partnerSummary={{
          region: partner360Data.geoRegion ?? '',
          country: partner360Data.country,
          partnerName: partner360Data.partnerName,
          pid: partner360Data.partnerId,
          locationId: partner360Data.locationId,
        }}
        onClose={onClose}
        onConfirmed={onActionExecute(selectedAction, false)}
      />

      <PlanReviewKeyInitiative
        selectedAction={selectedAction}
        partnerId={partner360Data.partnerId}
        programType={programType}
        fyear={fyear}
        onClose={onClose}
        onConfirmed={onActionExecute(selectedAction, false)}
      />

      <KeyInitiativesReviewModal
        show={selectedAction?.ActionType === CommitmentReminderActionTypeEnum.JBPPatKi}
        defaultMetricMode={reviewMetricMode}
        availableMetricModes={availableMetricModes}
        partnerId={partner360Data.partnerId}
        isDistributor={isDistributor}
        offering={programType}
        region={partner360Data.geoRegion ?? ''}
        country={partner360Data.country ?? ''}
        partnerName={partner360Data.partnerName ?? ''}
        locationId={partner360Data.locationId ?? ''}
        currentFiscalYear={fyear}
        onClose={onClose}
        onConfirmed={onActionExecute(selectedAction, false)}
        onGoToPlanning={goByActionLink}
      />

      <NetworkDebugSource name="Commitment reminder">
        <AcknowledgmentStatusDialog
          show={
            selectedAction?.ActionType === CommitmentReminderActionTypeEnum.JBPPatAcknowledgment
          }
          onClose={closeAcknowledgeAction}
          onUpdated={onActionExecuted}
          partnerId={partner360Data.partnerId}
          fYear={fyear}
          offering={programType}
          offeringLabel={programType}
          partnerSummary={{
            region: partner360Data.geoRegion ?? '',
            country: partner360Data.country,
            partnerName: partner360Data.partnerName,
            pid: partner360Data.partnerId,
            locationId: partner360Data.locationId,
          }}
        />
      </NetworkDebugSource>
    </div>
  )
}
